import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '@clerk/react';
import { useAuth } from '../context/AuthContext';
import { Loader2, Car, Check } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SelectRolePage() {
  const navigate = useNavigate();
  const { isSignedIn, user: clerkUser, isLoaded: clerkLoaded } = useUser();
  const { user, setUser, getToken } = useAuth();
  
  const pendingPhone = sessionStorage.getItem('pendingPhone');
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('Creating your account...');

  // If user already has a role (not pending), redirect them
  useEffect(() => {
    if (user && user.role && user.role !== 'pending') {
      if (user.role === 'admin') {
        navigate('/admin');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, navigate]);

  // Redirect to login if not signed in with Clerk
  useEffect(() => {
    if (clerkLoaded && !isSignedIn) {
      navigate('/login');
    }
  }, [clerkLoaded, isSignedIn, navigate]);

  // Auto-complete registration as customer
  useEffect(() => {
    if (clerkLoaded && isSignedIn && clerkUser && !loading && user?.role === 'pending') {
      completeRegistration();
    }
  }, [clerkLoaded, isSignedIn, clerkUser, user]);

  const completeRegistration = async () => {
    if (loading) return;
    
    setLoading(true);
    try {
      const token = await getToken();
      
      setStatus('Setting up your profile...');
      
      // All users are now customers by default
      const response = await axios.post(
        `${API}/auth/clerk/complete-registration`,
        { 
          role: 'customer',
          phone: pendingPhone || null,
          name: clerkUser.fullName || clerkUser.firstName || clerkUser.primaryEmailAddress?.emailAddress?.split('@')[0],
          email: clerkUser.primaryEmailAddress?.emailAddress
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          }
        }
      );
      
      // Clear session storage
      sessionStorage.removeItem('pendingRole');
      sessionStorage.removeItem('pendingPhone');
      
      // Update user in context
      setUser(response.data.user);
      
      setStatus('Account created!');
      toast.success('Welcome to RydQuest!');
      
      // Small delay to show success state
      setTimeout(() => {
        navigate('/dashboard');
      }, 500);
      
    } catch (error) {
      console.error('Registration error:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete registration');
      setLoading(false);
    }
  };

  if (!clerkLoaded) {
    return (
      <div className="min-h-[80vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-red-500" />
      </div>
    );
  }

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md text-center">
        {/* Logo */}
        <div className="w-20 h-20 rounded-2xl bg-red-500 flex items-center justify-center mx-auto mb-6">
          {status === 'Account created!' ? (
            <Check className="w-10 h-10 text-white" />
          ) : (
            <Car className="w-10 h-10 text-white" />
          )}
        </div>
        
        <h1 className="text-2xl font-bold text-white mb-2">
          {status === 'Account created!' ? 'Welcome!' : 'Almost there...'}
        </h1>
        
        <p className="text-neutral-400 mb-6">{status}</p>
        
        {loading && status !== 'Account created!' && (
          <Loader2 className="w-8 h-8 animate-spin text-red-500 mx-auto" />
        )}
        
        {clerkUser && (
          <p className="text-neutral-500 text-sm mt-4">
            {clerkUser.primaryEmailAddress?.emailAddress}
          </p>
        )}
      </div>
    </div>
  );
}
